import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  setServiceSubcategories,
  setServiceSubcategoriesLoading,
  setServiceSubcategoriesError,
} from "../redux/actions/dashboard";
import billingService from "../services/billingService";
import { useServiceCategories } from "./useServiceCategories";
import logger from "../utils/logger";


/**
 * Custom hook to manage service subcategories data with Redux caching
 * - Loads subcategories for all categories only once per session
 * - Caches data in Redux store
 * - Automatically cleared on logout
 *
 * Note: This fetches ALL subcategories for ALL categories at once
 * If you need subcategories for a specific category, filter the returned array
 */
export const useServiceSubcategories = () => {
  const dispatch = useDispatch();
  const { serviceCategories, serviceCategoriesLoading } = useServiceCategories();
  const { serviceSubcategories, serviceSubcategoriesLoading, serviceSubcategoriesError } = useSelector(state => ({
    serviceSubcategories: state.dashboard?.serviceSubcategories || [],
    serviceSubcategoriesLoading: state.dashboard?.serviceSubcategoriesLoading || false,
    serviceSubcategoriesError: state.dashboard?.serviceSubcategoriesError || null,
  }));

  const extractSubcategoriesFromCategories = categories => {
    return categories.flatMap(cat =>
      (cat.subcategories || []).map(sub => ({
        ...sub,
        categoryId: cat.id,
        categoryName: cat.name,
        categoryCode: cat.code,
      }))
    );
  };

  const loadServiceSubcategories = async () => {
    // If subcategories are already loaded, don't fetch again
    if (serviceSubcategories.length > 0) {
      return serviceSubcategories;
    }

    // If already loading, don't start another request
    if (serviceSubcategoriesLoading) {
      return serviceSubcategories;
    }

    // Wait for categories to load first
    if (serviceCategoriesLoading || serviceCategories.length === 0) {
      return serviceSubcategories;
    }

    try {
      dispatch(setServiceSubcategoriesLoading(true));
      dispatch(setServiceSubcategoriesError(null));

      // Try extracting subcategories from nested category data first
      const nestedSubcategories = extractSubcategoriesFromCategories(serviceCategories);
      if (nestedSubcategories.length > 0) {
        dispatch(setServiceSubcategories(nestedSubcategories));
        return nestedSubcategories;
      }

      // Fallback: fetch subcategories for each category individually
      const allSubcategories = [];
      for (const category of serviceCategories) {
        try {
          const response = await billingService.getServiceSubcategories(category.id, { limit: 1000 });
          if (response && response.data && response.data.data) {
            const subcategoriesWithCategoryId = response.data.data.map(sub => ({
              ...sub,
              categoryId: category.id,
              categoryName: category.name,
              categoryCode: category.code,
            }));
            allSubcategories.push(...subcategoriesWithCategoryId);
          }
        } catch (error) {
          logger.warn(`Failed to load subcategories for category ${category.id}:`, error);
        }
      }

      dispatch(setServiceSubcategories(allSubcategories));
      return allSubcategories;
    } catch (error) {
      logger.error("Error loading service subcategories:", error);
      dispatch(setServiceSubcategoriesError(error.message || "Failed to load service subcategories"));
      return [];
    }
  };

  // Auto-load subcategories when categories are loaded
  useEffect(() => {
    if (
      serviceCategories.length > 0 &&
      !serviceCategoriesLoading &&
      serviceSubcategories.length === 0 &&
      !serviceSubcategoriesLoading &&
      !serviceSubcategoriesError
    ) {
      loadServiceSubcategories();
    }
  }, [serviceCategories, serviceCategoriesLoading]);

  // Helper function to get subcategories for a specific category
  const getSubcategoriesByCategory = categoryId => {
    return serviceSubcategories.filter(sub => sub.categoryId === categoryId);
  };

  return {
    serviceSubcategories,
    serviceSubcategoriesLoading,
    serviceSubcategoriesError,
    loadServiceSubcategories,
    hasServiceSubcategories: serviceSubcategories.length > 0,
    getSubcategoriesByCategory,
  };
};
